import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import heroImage from '../assets/r9.webp';
import claimImage from '../assets/rty67.webp';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' },
  }),
};

const InsuranceContractRepricing = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Insurance Contract Repricing Services | MDeRCM</title>
        <meta 
          name="description" 
          content="Expert insurance contract repricing services. Recover underpaid claims, audit payer contracts and maximize reimbursements." 
        />
        <meta 
          name="keywords" 
          content="insurance contract repricing, healthcare revenue cycle management services, medical revenue cycle management services, revenue cycle management consulting services, healthcare cost reduction strategies, medical billing outsourcing services, revenue cycle management services companies, best revenue cycle management companies, revenue cycle management services usa, hospital revenue cycle management, how to improve revenue cycle management" 
        />
        <link rel="canonical" href="https://mdercm.com/insurance-contract-repricing" />
        <meta property="og:title" content="Insurance Contract Repricing Services | Healthcare Revenue Cycle Management" />
        <meta property="og:description" content="Optimize insurance contracts with AI-driven repricing solutions. Reduce errors by 50% and improve healthcare revenue cycle management." />
        <meta property="og:url" content="https://mdercm.com/insurance-contract-repricing" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* BREADCRUMB NAVIGATION */}
      <div style={{ 
        padding: '20px 5%', 
        backgroundColor: '#f7f9fb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '8px',
          fontSize: '14px',
          color: '#666'
        }}>
          <Link 
            to="/" 
            style={{ 
              color: '#666', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#F26522'}
            onMouseOut={(e) => e.target.style.color = '#666'}
          >
            Healthcare Revenue Cycle Management Services
          </Link>
          <span style={{ color: '#999' }}>/</span>
          <a 
            href="https://mdercm.com/" 
            style={{ 
              color: '#666', 
              textDecoration: 'none',
              transition: 'color 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#F26522'}
            onMouseOut={(e) => e.target.style.color = '#666'}
          >
            AI Solutions
          </a>
          <span style={{ color: '#999' }}>/</span>
          <span style={{ color: '#F26522', fontWeight: '600' }}>Insurance Contract Repricing</span>
        </div>
      </div>

      {/* HERO SECTION */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '650px', zIndex: 2 }}>
          <motion.h1
            style={{
              fontSize: 'clamp(24px, 5vw, 42px)',
              fontWeight: 700,
              marginBottom: '20px',
              lineHeight: 1.4,
            }}
          >
            Healthcare Revenue Cycle Management Services - Insurance Contract Repricing Solutions
          </motion.h1>
          <motion.p
            style={{
              fontSize: 'clamp(14px, 2vw, 18px)',
              marginBottom: '30px',
              color: '#dddddd',
            }}
          >
            Leading <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</a> offering AI-driven insurance contract repricing. Our <strong>medical revenue cycle management services</strong> help healthcare providers implement effective <strong>healthcare cost reduction strategies</strong> while ensuring pricing accuracy and compliance.
          </motion.p>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '30px',
              marginBottom: '30px',
            }}
          >
            {[
              ['50%', 'Reduction in Pricing Errors'],
              ['10x', 'Faster Contract Review Cycles'],
              ['30%', 'Increase in Compliance Accuracy'],
            ].map(([value, label], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 160px',
                  maxWidth: '220px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#F26522' }}>{value}</div>
                <div style={{ fontSize: '16px', color: '#ffffff' }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* INTRO SECTION */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
        <h2
          style={{
            textAlign: 'center',
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: '700',
            color: '#001F2B',
            marginBottom: '30px',
          }}
        >
          Best Revenue Cycle Management Companies - Transform Your Contract Management
        </h2>
        
        <p style={{ 
          textAlign: 'center', 
          fontSize: '18px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, we provide comprehensive <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none' }}>revenue cycle management consulting services</a> that include advanced insurance contract repricing and <strong>medical billing outsourcing services</strong>.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '40px',
          }}
        >
          <div style={{ flex: '1 1 500px', minWidth: '280px', maxWidth: '600px' }}>
            <h3
              style={{
                fontSize: '24px',
                fontWeight: '700',
                marginBottom: '20px',
                color: '#001F2B',
              }}
            >
              Hospital Revenue Cycle Management Excellence with MDeRCM
            </h3>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a>'s AI-driven solution offers complete visibility into all your insurance contracts and pricing actions. Our <strong>outsourced revenue cycle management services</strong> scan contracts to identify anomalies like outdated clauses, pricing errors, and discrepancies in terms, reducing the risk of mistakes and ensuring accuracy.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              Our AI provides optimized pricing strategies based on industry insights, competitor analysis, and market demands. We help healthcare organizations understand <strong>how to improve revenue cycle management</strong> by detecting undervalued policies and overpriced premiums, ensuring competitive and profitable pricing at all times.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>
              <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none' }}>MDeRCM</a>'s system automates key processes, such as contract renewals and pricing updates. Our <strong>revenue cycle management services for hospitals</strong> save your team time and streamline workflows while implementing proven <strong>healthcare cost reduction strategies</strong>.
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            style={{
              flex: '1 1 400px',
              minWidth: '280px',
              maxWidth: '500px',
              textAlign: 'center',
            }}
          >
            <img
              src={claimImage}
              alt="Healthcare Revenue Cycle Management Services - Insurance Contract Repricing"
              style={{
                width: '100%',
                borderRadius: '20px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* RELATED SERVICES SECTION */}
      <div style={{ padding: '60px 5%', backgroundColor: '#f0f4f8' }}>
        <h2
          style={{
            textAlign: 'center',
            fontSize: 'clamp(22px, 4vw, 32px)',
            fontWeight: '700',
            color: '#001F2B',
            marginBottom: '40px',
          }}
        >
          Complete Revenue Cycle Management Services USA
        </h2>
        <p style={{ 
          textAlign: 'center', 
          fontSize: '16px', 
          color: '#555', 
          marginBottom: '40px',
          maxWidth: '800px',
          margin: '0 auto 40px'
        }}>
          Explore our comprehensive suite of <strong>medical billing and coding outsourcing</strong> services designed to optimize your entire revenue cycle:
        </p>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <motion.div
            whileHover={{ scale: 1.05, boxShadow: '0 12px 30px rgba(242, 101, 34, 0.2)' }}
            style={{
              backgroundColor: '#ffffff',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              textAlign: 'center',
              border: '2px solid transparent',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ fontSize: '42px', marginBottom: '15px' }}>✅</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#001F2B', marginBottom: '12px' }}>
              Eligibility Check Services
            </h3>
            <p style={{ fontSize: '15px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
              Verify patient insurance eligibility in real-time to reduce claim denials and improve cash flow.
            </p>
            <Link 
              to="/healthcare/ai/eligibility-check" 
              style={{ 
                color: '#F26522', 
                textDecoration: 'none', 
                fontWeight: '600',
                fontSize: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Learn More →
            </Link>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, boxShadow: '0 12px 30px rgba(242, 101, 34, 0.2)' }}
            style={{
              backgroundColor: '#ffffff',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              textAlign: 'center',
              border: '2px solid transparent',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ fontSize: '42px', marginBottom: '15px' }}>🔍</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#001F2B', marginBottom: '12px' }}>
              Policy Status Verification
            </h3>
            <p style={{ fontSize: '15px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
              Automated verification of insurance policy status for accurate billing and reduced rejections.
            </p>
            <Link 
              to="/healthcare/ai/policy-status-verification" 
              style={{ 
                color: '#F26522', 
                textDecoration: 'none', 
                fontWeight: '600',
                fontSize: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Learn More →
            </Link>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, boxShadow: '0 12px 30px rgba(242, 101, 34, 0.2)' }}
            style={{
              backgroundColor: '#ffffff',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              textAlign: 'center',
              border: '2px solid transparent',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ fontSize: '42px', marginBottom: '15px' }}>📋</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#001F2B', marginBottom: '12px' }}>
              Prior Authorization
            </h3>
            <p style={{ fontSize: '15px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
              Streamline prior authorization processes with AI-powered automation and faster approvals.
            </p>
            <Link 
              to="/healthcare/ai/prior-authorization" 
              style={{ 
                color: '#F26522', 
                textDecoration: 'none', 
                fontWeight: '600',
                fontSize: '16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Learn More →
            </Link>
          </motion.div>
        </div>
      </div>

      {/* CAPABILITIES */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '20px',
          }}
        >
          Medical Billing Outsourcing Companies - Our Capabilities
        </motion.h2>
        
        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px'
        }}>
          As leading <strong>medical billing outsourcing companies</strong>, we leverage advanced AI technology to deliver comprehensive <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none' }}>healthcare revenue cycle management services</a>:
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '24px',
          }}
        >
          {[
            ['🔍', 'Automated Contract Review', 'Scans contracts for errors, inconsistencies, and out-of-date clauses using AI-powered analysis.'],
            ['🧠', 'AI-Powered Pricing Models', 'Suggests competitive yet profitable pricing models based on market trends and healthcare cost reduction strategies.'],
            ['🔄', 'Dynamic Repricing', 'Adjusts prices continuously based on real-time market conditions and competitor data analysis.'],
            ['⚙️', 'Compliance Monitoring', 'Ensures contracts comply with industry regulations and reduces penalties through automated checks.'],
            ['📊', 'Real-Time Market Analysis', 'Provides actionable insights into market trends and competitor actions to optimize pricing decisions.'],
            ['⚡', 'Fast Track Approvals', 'Speeds up contract approvals by automating manual processes and eliminating costly errors.'],
          ].map(([icon, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                width: '100%',
                maxWidth: '350px',
                backgroundColor: '#f7f9fb',
                padding: '28px',
                borderRadius: '16px',
                boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                textAlign: 'center',
                border: '1px solid #e5e7eb'
              }}
            >
              <div style={{ fontSize: '42px', marginBottom: '15px' }}>{icon}</div>
              <h4
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  marginBottom: '12px',
                  color: '#001F2B',
                }}
              >
                {title}
              </h4>
              <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.6' }}>{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* HOW TO IMPROVE SECTION */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '30px',
          }}
        >
          How to Improve Revenue Cycle Management with MDeRCM
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px',
          lineHeight: '1.8'
        }}>
          Our <strong>revenue cycle management services USA</strong> provide proven strategies to optimize your operations. Learn <strong>how to improve revenue cycle management</strong> with our comprehensive approach that combines <strong>medical billing and coding outsourcing</strong> with advanced contract repricing:
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          {[
            ['⚡', 'Faster Contract Processing', 'Reduces contract review and approval time by up to 10x, accelerating your entire workflow and improving cash flow.'],
            ['💸', 'Optimized Pricing Strategy', 'Automatically adjusts pricing to match market trends, competitor analysis, and improve profitability with data-driven insights.'],
            ['🕐', 'Real-Time Adjustments', 'Ensures continuous repricing based on current market dynamics, regulatory changes, and competitive landscape.'],
            ['📈', 'Scalable & Consistent', 'Handles large volumes of contracts without sacrificing quality or speed - perfect for growing healthcare organizations.'],
            ['🤝', 'Improved Client Trust', 'Delivers transparent, competitive pricing that fosters long-term relationships and patient satisfaction.'],
            ['📊', 'Data-Led Insights', 'Provides actionable analytics and reporting to drive better strategic decisions in pricing, contracts, and revenue optimization.'],
          ].map(([icon, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                flex: '1 1 280px',
                maxWidth: '380px',
                backgroundColor: '#ffffff',
                padding: '28px',
                borderRadius: '12px',
                boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                textAlign: 'left',
                border: '1px solid #e5e7eb'
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '15px' }}>{icon}</div>
              <h4
                style={{
                  fontSize: '19px',
                  fontWeight: '700',
                  marginBottom: '12px',
                  color: '#001F2B',
                }}
              >
                {title}
              </h4>
              <p style={{ fontSize: '15px', color: '#444', lineHeight: '1.7' }}>{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* HEALTHCARE COST REDUCTION STRATEGIES */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '30px',
          }}
        >
          Healthcare Cost Reduction Consulting & Strategies
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px',
          lineHeight: '1.8'
        }}>
          As a trusted provider of <strong>healthcare cost reduction consulting</strong>, <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a> implements proven <strong>healthcare cost reduction strategies</strong> that deliver measurable results:
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '30px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            backgroundColor: '#f7f9fb',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#F26522', marginBottom: '15px' }}>
              Reduce Administrative Costs
            </h3>
            <p style={{ fontSize: '16px', color: '#444', lineHeight: '1.7' }}>
              Automate contract management and repricing processes to eliminate manual errors and reduce operational expenses by up to 40%.
            </p>
          </div>

          <div style={{
            backgroundColor: '#f7f9fb',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#F26522', marginBottom: '15px' }}>
              Optimize Reimbursements
            </h3>
            <p style={{ fontSize: '16px', color: '#444', lineHeight: '1.7' }}>
              Ensure accurate contract pricing and terms to maximize reimbursements and minimize revenue leakage across all payer contracts.
            </p>
          </div>

          <div style={{
            backgroundColor: '#f7f9fb',
            padding: '30px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#F26522', marginBottom: '15px' }}>
              Prevent Revenue Loss
            </h3>
            <p style={{ fontSize: '16px', color: '#444', lineHeight: '1.7' }}>
              Identify undervalued services and pricing errors before they impact your bottom line with AI-powered contract analysis.
            </p>
          </div>
        </div>
      </div>

      {/* EXPLORE COMPLETE SERVICES */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '30px',
          }}
        >
          Explore Our Complete Healthcare Revenue Cycle Management Services
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px',
          lineHeight: '1.8'
        }}>
          <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a> offers end-to-end <strong>medical revenue cycle management services</strong> that cover every aspect of your revenue cycle. Our comprehensive <strong>medical billing and coding outsourcing</strong> solutions ensure maximum efficiency and profitability.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <style>
            {`
              @media (max-width: 1200px) {
                .services-grid {
                  grid-template-columns: repeat(3, 1fr) !important;
                }
              }
              @media (max-width: 900px) {
                .services-grid {
                  grid-template-columns: repeat(2, 1fr) !important;
                }
              }
              @media (max-width: 600px) {
                .services-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}
          </style>
          {[
            {
              icon: '✅',
              title: 'Patient Eligibility Verification',
              desc: 'Real-time insurance eligibility checks to prevent claim denials',
              link: '/healthcare/ai/eligibility-check'
            },
            {
              icon: '🔍',
              title: 'Policy Status Verification',
              desc: 'Automated verification of active insurance coverage and benefits',
              link: '/healthcare/ai/policy-status-verification'
            },
            {
              icon: '📋',
              title: 'Prior Authorization Services',
              desc: 'Streamlined authorization process with AI-powered automation',
              link: '/healthcare/ai/prior-authorization'
            },
            {
              icon: '📱',
              title: 'Patient Intake & Reminders',
              desc: 'Automated payment reminders and on-time payment systems',
              link: '/healthcare/ai/patient-intake'
            },
            {
              icon: '🛡️',
              title: 'Denial Management Services',
              desc: 'Reduce denials by 70% with smart recovery strategies',
              link: '/healthcare/ai/denial-management'
            },
            {
              icon: '💰',
              title: 'Payment Posting',
              desc: '50x faster automated payment reconciliation',
              link: '/healthcare/ai/payment-posting'
            },
            {
              icon: '📊',
              title: 'Accounts Receivable Management',
              desc: '60% faster collections with AI-driven AR management',
              link: '/healthcare/ai/accounts-receivable'
            },
            {
              icon: '🔒',
              title: 'Compliance Management',
              desc: 'Stay audit-ready 24/7 with continuous monitoring',
              link: '/healthcare/ai/compliance-agent'
            },
          ].map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                textAlign: 'center',
                border: '1px solid #e5e7eb',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                scale: 1.05, 
                boxShadow: '0 12px 30px rgba(242, 101, 34, 0.2)',
                borderColor: '#F26522'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{service.icon}</div>
              <h3 style={{ 
                fontSize: '19px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {service.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6', 
                marginBottom: '20px' 
              }}>
                {service.desc}
              </p>
              {service.link.startsWith('http') ? (
                <a 
                  href={service.link}
                  style={{ 
                    color: '#F26522', 
                    textDecoration: 'none', 
                    fontWeight: '600',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Learn More →
                </a>
              ) : (
                <Link 
                  to={service.link}
                  style={{ 
                    color: '#F26522', 
                    textDecoration: 'none', 
                    fontWeight: '600',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Learn More →
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* WHY CHOOSE MDERCM */}
      <div style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Why Choose MDeRCM for Outsourced Revenue Cycle Management Services?
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, <a href="https://mdercm.com/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</a> provides industry-leading <strong>revenue cycle management services USA</strong> with proven results and dedicated support.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <style>
            {`
              @media (max-width: 1200px) {
                .why-choose-grid {
                  grid-template-columns: repeat(3, 1fr) !important;
                }
              }
              @media (max-width: 900px) {
                .why-choose-grid {
                  grid-template-columns: repeat(2, 1fr) !important;
                }
              }
              @media (max-width: 600px) {
                .why-choose-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}
          </style>
          {[
            {
              icon: '🏆',
              title: 'Industry-Leading Expertise',
              desc: 'Over 10+ years of experience in healthcare revenue cycle management with proven track record of success.'
            },
            {
              icon: '🤖',
              title: 'Advanced AI Technology',
              desc: 'Cutting-edge AI and machine learning algorithms that continuously improve accuracy and efficiency.'
            },
            {
              icon: '💵',
              title: 'Maximize Revenue',
              desc: 'Increase revenue by up to 30% through optimized pricing, reduced denials, and faster collections.'
            },
            {
              icon: '⚡',
              title: '24/7 Support',
              desc: 'Round-the-clock dedicated support team ensuring seamless operations and quick issue resolution.'
            },
            {
              icon: '🔒',
              title: 'HIPAA Compliant',
              desc: 'Fully compliant with HIPAA and industry regulations, ensuring data security and patient privacy.'
            },
            {
              icon: '📈',
              title: 'Scalable Solutions',
              desc: 'Flexible services that grow with your practice, from small clinics to large hospital systems.'
            },
            {
              icon: '💡',
              title: 'Transparent Reporting',
              desc: 'Real-time dashboards and detailed analytics providing complete visibility into your revenue cycle.'
            },
            {
              icon: '🎯',
              title: 'Proven ROI',
              desc: 'Measurable results with most clients seeing positive ROI within 3-6 months of implementation.'
            },
          ].map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{reason.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '12px' 
              }}>
                {reason.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {reason.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '60px'
        }}>
          <Link 
            to="/free-trial"
            style={{
              backgroundColor: '#F26522',
              color: '#ffffff',
              padding: '18px 50px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '18px',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
            }}
          >
            Schedule a Free Consultation
          </Link>
        </div>
      </div>

      <FAQSection />
      <ConsultationCTA />
    </>
  );
};

export default InsuranceContractRepricing;